import { AuthenticatedAPIClient, APIResponse } from "@/lib/authenticated-api";
import type { PermissionDefinition } from "@/lib/permissions";

export interface UserManagementData {
	excessUsers: Array<{
		id: string;
		name: string;
		email: string;
		role: string | null;
		createdAt: Date;
	}>;
	missingUsers: Array<{
		email: string;
		suggestedRole: string;
	}>;
}

export class UserAPIService {
	/**
	 * Get user management data including surplus and missing users
	 */
	static async getUserManagement(): Promise<APIResponse<UserManagementData>> {
		return AuthenticatedAPIClient.get<UserManagementData>("/users/management");
	}

	/**
	 * Admin sửa tên / mã CB của một tài khoản khác.
	 *
	 * Không dùng `authClient.updateUser` — endpoint đó luôn ghi vào tài khoản
	 * đang đăng nhập, nên admin sửa người khác sẽ đổi nhầm tên của chính mình.
	 */
	static async updateProfile(
		userId: string,
		profile: { name?: string; MaCB?: string },
	): Promise<APIResponse<any>> {
		return AuthenticatedAPIClient.patch(`/users/${userId}`, profile);
	}

	/**
	 * Update user role
	 */
	static async updateUserRole(userId: string, role: string): Promise<APIResponse<any>> {
		return AuthenticatedAPIClient.patch(`/users/${userId}/role`, { role });
	}

	/**
	 * Gán hoặc gỡ khoa của một tài khoản. `null` nghĩa là không thuộc khoa nào.
	 *
	 * Đây là đường ghi `faculty_code` duy nhất được phép: server validate mã khoa
	 * còn tồn tại và còn hoạt động, rồi mới ghi. Mọi đường khác — kể cả
	 * `/auth/admin/update-user` của better-auth — bị chặn ở tầng Prisma.
	 */
	static async updateFacultyCode(userId: string, facultyCode: string | null) {
		return AuthenticatedAPIClient.patch(`/users/${userId}/faculty-code`, {
			faculty_code: facultyCode,
		});
	}

	/**
	 * Cấp hoặc thu hồi MỘT quyền cho một tài khoản.
	 *
	 * Đường ghi `permissions` duy nhất được phép, cùng lý do với
	 * `updateFacultyCode`: đây là cấp quyền, không phải sửa hồ sơ, nên nó có
	 * endpoint riêng chỉ admin gọi được và bị khoá ở tầng Prisma cho mọi đường
	 * khác. Server đọc object hiện tại rồi chỉ đặt đúng khoá này, nên gọi hai lần
	 * cho hai quyền không làm quyền trước bị xoá.
	 */
	static async updatePermission(userId: string, key: string, granted: boolean) {
		return AuthenticatedAPIClient.patch(`/users/${userId}/permissions`, { key, granted });
	}

	/** Danh mục quyền + nhãn tiếng Việt, dựng từ server để thêm quyền chỉ sửa một chỗ. */
	static async getPermissionCatalog(): Promise<PermissionDefinition[]> {
		const response = await AuthenticatedAPIClient.get("/users/permission-catalog");
		return (response.data ?? []) as PermissionDefinition[];
	}

	/**
	 * Delete user
	 */
	static async deleteUser(userId: string): Promise<APIResponse<any>> {
		return AuthenticatedAPIClient.delete(`/users/${userId}`);
	}
}
